import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Phone, Mail, MapPin, Building, Globe, Star, 
  Edit2, Trash2, Save, X, Loader2, MessageSquare
} from 'lucide-react';
import API from '@/utils/api';
import { toast } from 'sonner';
import { 
  ContactActionRow,
  ClickablePhone,
  ClickableEmail,
  ClickableAddress 
} from '@/components/native/ContactActionButtons';

const CATEGORY_COLORS = {
  personal: { bg: 'bg-blue-100', text: 'text-blue-700' },
  work: { bg: 'bg-purple-100', text: 'text-purple-700' },
  medical: { bg: 'bg-green-100', text: 'text-green-700' },
  emergency: { bg: 'bg-red-100', text: 'text-red-700' },
  service: { bg: 'bg-amber-100', text: 'text-amber-700' },
  other: { bg: 'bg-slate-100', text: 'text-slate-700' },
};

const ContactDetailSheet = ({ 
  contact, 
  open, 
  onOpenChange, 
  onContactUpdated, 
  onContactDeleted 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        category: contact.category || 'personal',
        company: contact.company || '',
        phone: contact.phone || '',
        email: contact.email || '',
        address: contact.address || '',
        website: contact.website || '',
        notes: contact.notes || '',
        is_favorite: contact.is_favorite || false,
      });
    }
  }, [contact]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setLoading(true);
    try {
      await API.put(`/contacts/${contact.id}`, formData);
      setIsEditing(false);
      onContactUpdated();
    } catch (error) {
      toast.error('Failed to update contact');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await onContactDeleted();
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenWebsite = () => {
    if (contact?.website) {
      let url = contact.website;
      if (!url.startsWith('http')) {
        url = `https://${url}`;
      }
      window.open(url, '_blank');
    }
  };

  if (!contact) return null;

  const categoryColor = CATEGORY_COLORS[contact.category] || CATEGORY_COLORS.other;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <span>Contact Details</span>
            <div className="flex items-center gap-2">
              {!isEditing ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    data-testid="edit-contact-btn"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-red-600 hover:text-red-700"
                    data-testid="delete-contact-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSave}
                    disabled={loading}
                    className="text-green-600"
                    data-testid="save-changes-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                  </Button>
                </>
              )}
            </div>
          </SheetTitle>
        </SheetHeader>

        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="my-4 p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm text-red-700 mb-3">
              Are you sure you want to delete this contact?
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleDelete}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700"
              >
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Delete
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 space-y-6">
          {/* Header with Avatar */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-pink-100 flex items-center justify-center">
              <span className="text-2xl font-semibold text-pink-700">
                {contact.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1">
              {isEditing ? (
                <Input
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="text-lg font-semibold"
                />
              ) : (
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  {contact.name}
                  {contact.is_favorite && (
                    <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                  )}
                </h3>
              )}
              <Badge className={`${categoryColor.bg} ${categoryColor.text} mt-1`}>
                {isEditing ? (
                  <select
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    className="bg-transparent border-none text-xs"
                  >
                    <option value="personal">Personal</option>
                    <option value="work">Work</option>
                    <option value="medical">Medical</option>
                    <option value="emergency">Emergency</option>
                    <option value="service">Service</option>
                    <option value="other">Other</option>
                  </select>
                ) : (
                  contact.category
                )}
              </Badge>
            </div>
          </div>

          {/* Quick Actions using Native Integrations */}
          {!isEditing && (
            <ContactActionRow
              phone={contact.phone}
              email={contact.email}
              address={contact.address}
              businessName={contact.company}
              className="pb-2"
              data-testid="contact-action-row"
            />
          )}

          {/* Business Card Image */}
          {contact.business_card_image && (
            <div className="rounded-lg overflow-hidden border border-slate-200">
              <img 
                src={contact.business_card_image} 
                alt="Business card" 
                className="w-full"
              />
            </div>
          )}

          {/* Contact Details */}
          <div className="space-y-4">
            {/* Company */}
            <div className="flex items-start gap-3">
              <Building className="h-5 w-5 text-slate-400 mt-0.5" />
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Company</Label>
                {isEditing ? (
                  <Input
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    placeholder="Company name"
                  />
                ) : (
                  <p className="text-sm text-slate-800">
                    {contact.company || <span className="text-slate-400">Not set</span>}
                  </p>
                )}
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-slate-400 mt-0.5" />
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Phone</Label>
                {isEditing ? (
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="Phone number"
                    data-testid="edit-phone-input"
                  />
                ) : contact.phone ? (
                  <ClickablePhone 
                    phone={contact.phone} 
                    showIcon={false}
                    className="text-sm"
                    data-testid="clickable-phone"
                  />
                ) : (
                  <p className="text-sm text-slate-400">Not set</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-slate-400 mt-0.5" />
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Email</Label>
                {isEditing ? (
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="Email address"
                    data-testid="edit-email-input"
                  />
                ) : contact.email ? (
                  <ClickableEmail 
                    email={contact.email}
                    showIcon={false}
                    className="text-sm"
                    data-testid="clickable-email"
                  />
                ) : (
                  <p className="text-sm text-slate-400">Not set</p>
                )}
              </div>
            </div>

            {/* Address */}
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Address</Label>
                {isEditing ? (
                  <Textarea
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="Address"
                    rows={2}
                    data-testid="edit-address-input"
                  />
                ) : contact.address ? (
                  <ClickableAddress 
                    address={contact.address}
                    businessName={contact.company}
                    showIcon={false}
                    className="text-sm"
                    data-testid="clickable-address"
                  />
                ) : (
                  <p className="text-sm text-slate-400">Not set</p>
                )}
              </div>
            </div>

            {/* Website */}
            <div className="flex items-start gap-3">
              <Globe className="h-5 w-5 text-slate-400 mt-0.5" />
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Website</Label>
                {isEditing ? (
                  <Input
                    value={formData.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    placeholder="Website URL"
                  />
                ) : contact.website ? (
                  <button
                    onClick={handleOpenWebsite}
                    className="text-sm text-blue-600 hover:underline text-left"
                  >
                    {contact.website}
                  </button>
                ) : (
                  <p className="text-sm text-slate-400">Not set</p>
                )}
              </div>
            </div>

            {/* Notes */}
            {(isEditing || contact.notes) && (
              <div className="pt-4 border-t border-slate-100">
                <Label className="text-xs text-slate-500">Notes</Label>
                {isEditing ? (
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Add notes"
                    rows={3}
                    className="mt-1"
                  />
                ) : (
                  <p className="text-sm text-slate-600 whitespace-pre-wrap mt-1">
                    {contact.notes}
                  </p>
                )}
              </div>
            )}

            {/* Favorite toggle in edit mode */}
            {isEditing && (
              <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
                <input
                  type="checkbox"
                  id="edit_favorite"
                  checked={formData.is_favorite}
                  onChange={(e) => handleChange('is_favorite', e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="edit_favorite" className="cursor-pointer">
                  Mark as favorite
                </Label>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ContactDetailSheet;
